// core/BaseSkill.js
// Base class for skills with common tool management functionality
// Eliminates duplicate code for tool initialization, buff calculation, and speed multipliers

import { gameState } from '../game/gameState.js';
import { calculateEffectiveSpeed } from '../utils/skillSpeed.js';

/**
 * Base class for skills that use tools
 * Provides common functionality for tool management and speed calculation
 *
 * @example
 * class MiningSkill extends BaseSkill {
 *     constructor() {
 *         super(SKILLS.MINING, PICKAXES, 'BRONZE');
 *     }
 *
 *     // Optionally override for skill-specific speed bonuses
 *     getEffectiveSpeedMultiplier() {
 *         let multiplier = super.getEffectiveSpeedMultiplier();
 *         // Add skill-specific bonuses here
 *         return multiplier;
 *     }
 * }
 */
export class BaseSkill {
    /**
     * Create a new skill instance
     * @param {string} skillId - The skill ID (from SKILLS constant)
     * @param {Object} toolsObject - The tools/rods/pickaxes constant object
     * @param {string} defaultToolKey - Key of the default tool (e.g., 'BASIC', 'BRONZE')
     */
    constructor(skillId, toolsObject, defaultToolKey = 'BASIC') {
        this.skillId = skillId;
        this.toolsObject = toolsObject;
        this.defaultToolKey = defaultToolKey;
        this.defaultTool = toolsObject[defaultToolKey];
        this.toolsArray = Object.values(toolsObject);

        // Completion callback for UI updates
        this.onComplete = null;

        // Initialize starting tool
        this.initializeStartingTool();
    }

    /**
     * Initialize the starting tool - ensure it's owned and equipped
     * Called during construction and can be called on game load
     */
    initializeStartingTool() {
        if (!gameState.hasPurchasedTool(this.skillId, this.defaultTool.id)) {
            gameState.purchaseTool(this.skillId, this.defaultTool.id, 0);
        }
        if (!gameState.getEquippedTool(this.skillId)) {
            gameState.equipTool(this.skillId, this.defaultTool.id);
        }
    }

    /**
     * Get the currently equipped tool object
     * @returns {Object} The tool object
     */
    getCurrentTool() {
        const equippedId = gameState.getEquippedTool(this.skillId);
        return this.toolsArray.find(t => t.id === equippedId) || this.defaultTool;
    }

    /**
     * Get the highest tier owned tool (determines actual speed buff)
     * @returns {Object} The highest tier tool owned
     */
    getActiveBuff() {
        return gameState.getHighestPurchasedTool(this.skillId, this.toolsArray) || this.defaultTool;
    }

    /**
     * Calculate effective speed multiplier including tool, mastery, and talent bonuses
     * Override this method in subclasses to add skill-specific bonuses
     * @param {Object} options - Options to pass to calculateEffectiveSpeed
     * @returns {number} The effective speed multiplier
     */
    getEffectiveSpeedMultiplier(options = {}) {
        const activeTool = this.getActiveBuff();
        return calculateEffectiveSpeed(this.skillId, activeTool, options);
    }

    /**
     * Get the skill's current level from gameState
     * @returns {number} Current skill level
     */
    getLevel() {
        return gameState.getSkill(this.skillId)?.level || 1;
    }

    /**
     * Get the skill's current XP from gameState
     * @returns {number} Current skill XP
     */
    getXP() {
        return gameState.getSkill(this.skillId)?.xp || 0;
    }
}

export default BaseSkill;
